import { useEffect, useMemo } from "react";
import PlanUsageChart from "../../../../../local_modules/plan-usage-chart/src/PlanUsageChart";
import { useReportsStore } from "../../../../../hooks/redux/reports";
import { useSubscriptionPlansStore } from "../../../../../hooks/redux/subscriptionPlans";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";

const formatBillingMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getLastTwelveBillingMonths = () => {
  const months = [];
  const currentMonth = new Date();

  currentMonth.setDate(1);

  for (let offset = 11; offset >= 0; offset -= 1) {
    const month = new Date(currentMonth);
    month.setMonth(currentMonth.getMonth() - offset);
    months.push(formatBillingMonth(month));
  }

  return months;
};

const normalizePlanUsageHistory = (history = []) => {
  const usageByMonth = history.reduce((acc, row) => {
    if (!row?.billing_month) {
      return acc;
    }

    acc[row.billing_month] = Number(row.widget_views) || 0;
    return acc;
  }, {});

  return getLastTwelveBillingMonths().map((billingMonth) => ({
    billing_month: billingMonth,
    widget_views: usageByMonth[billingMonth] || 0,
  }));
};

const getPlanPrice = (plan, preferredInterval) => {
  const currencies = Object.keys(plan?.prices || {});

  for (const currency of currencies) {
    const matchingPrice = plan.prices[currency]?.find(
      (price) => price.recurring?.interval === preferredInterval && price.active
    );

    if (matchingPrice?.unit_amount != null) {
      return matchingPrice.unit_amount / 100;
    }
  }

  return null;
};

const mapPlanForChart = (plan, preferredInterval) => {
  if (!plan) {
    return null;
  }

  const quota = Number(plan.metadata?.views_limit);

  if (!Number.isFinite(quota) || (quota <= 0 && quota !== -1)) {
    return null;
  }

  return {
    name: plan.name,
    quota,
    price: getPlanPrice(plan, preferredInterval),
    code: plan.metadata?.app_plan_code,
  };
};

const PlanUsageChartContainer = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const { reports, fetchPlanUsageStats } = useReportsStore();
  const { subscriptionPlans, fetchPlans } = useSubscriptionPlansStore();

  const currentSubscription = currentOrganization.subscription;
  const preferredInterval = currentSubscription?.is_annual ? "year" : "month";
  const currentPlanCode = currentSubscription?.plan_code;
  const currentPlanQuota = Number(currentSubscription?.quotas?.views_limit) || 0;
  
  useEffect(() => {
    if (
      !subscriptionPlans.fetch &&
      !subscriptionPlans.fetchSuccess &&
      !subscriptionPlans.fetchError
    ) {
      fetchPlans();
    }
  }, [
    fetchPlans,
    subscriptionPlans.fetch,
    subscriptionPlans.fetchError,
    subscriptionPlans.fetchSuccess,
  ]);

  useEffect(() => {
    if (!currentSubscription) {
      return;
    }

    fetchPlanUsageStats();
  }, [currentSubscription, fetchPlanUsageStats]);

  const allPlans = useMemo(
    () =>
      subscriptionPlans.collection
        .map((plan) => mapPlanForChart(plan, preferredInterval))
        .filter(Boolean)
        .sort((a, b) => {
          if (a.quota === -1) return 1;
          if (b.quota === -1) return -1;
          return a.quota - b.quota;
        }),
    [preferredInterval, subscriptionPlans.collection]
  );

  const currentPlan = useMemo(() => {
    return {
      name: currentSubscription?.name || "Current plan",
      quota: currentPlanQuota,
      price: Number(currentSubscription?.price) || null,
      code: currentPlanCode,
    };
  }, [currentPlanCode, currentPlanQuota, currentSubscription]);

  const billingDate = useMemo(() => {
    if (!currentSubscription?.expiry) {
      return null;
    }

    return new Date(currentSubscription.expiry * 1000).toISOString();
  }, [currentSubscription]);

  const history = useMemo(
    () => normalizePlanUsageHistory(reports.planUsageStatsCollection),
    [reports.planUsageStatsCollection]
  );
  
  return (
    <PlanUsageChart
      title="Widget Views"
      currentPlan={currentPlan}
      allPlans={allPlans}
      billingDate={billingDate}
      billingPath="/billing"
      history={history}
      loading={reports.fetchPlanUsageStats || subscriptionPlans.fetch}
      error={reports.fetchPlanUsageStatsError?.message || null}
    />
  );
};

export default PlanUsageChartContainer;
